import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Holes } from '../../config';
import { showPoints } from '../../utils/gameFormats';
import { getTranslation } from '../../utils/getTranslation';
import { insertIntoArray } from '../../utils/insertIntoArray';
import HiddenHole from '../icons/HiddenHole';
import { convertPhcpToString } from '../../utils/convertHcpToString';
import { getFormattedDate } from '../../utils/date';
import { convertHcpToString } from '../../utils/convertHcpToString';
export default function ScoreTableRoundStats(_a) {
    var leaderboard = _a.leaderboard, player = _a.player, roundNumber = _a.roundNumber, isMultiRound = _a.isMultiRound, _b = _a.holesToShow, holesToShow = _b === void 0 ? Holes.FULL18 : _b, gameFormatId = _a.gameFormatId, isScratch = _a.isScratch, isWeekly = _a.isWeekly, _c = _a.units, units = _c === void 0 ? 'metric' : _c, timezone = _a.timezone;
    var intl = useIntl();
    var playerRound = useMemo(function () { return player.rounds[roundNumber - 1]; }, [player, roundNumber]);
    var leaderboardRound = useMemo(function () { return leaderboard === null || leaderboard === void 0 ? void 0 : leaderboard.rounds[roundNumber - 1]; }, [leaderboard, roundNumber]);
    var hiddenHoleCount = useMemo(function () { return playerRound.hidden.filter(function (h) { return h; }).length; }, [playerRound]);
    var getParData = function () {
        var holePars = (leaderboardRound === null || leaderboardRound === void 0 ? void 0 : leaderboardRound.par) || [];
        var parOut = (leaderboardRound === null || leaderboardRound === void 0 ? void 0 : leaderboardRound.parOut) || '';
        var parIn = (leaderboardRound === null || leaderboardRound === void 0 ? void 0 : leaderboardRound.parIn) || '';
        var parTotal = (leaderboardRound === null || leaderboardRound === void 0 ? void 0 : leaderboardRound.parTotal) || '';
        var parInfo = insertIntoArray(holePars, 9, parOut);
        parInfo.push(parIn);
        parInfo.push(parTotal);
        if (holesToShow === Holes.FRONT9) {
            return insertIntoArray(parInfo.splice(0, 10), 11, '');
        }
        else if (holesToShow === Holes.BACK9) {
            return parInfo.splice(10);
        }
        return parInfo;
    };
    var getScoreData = function () {
        var holeScores = (playerRound === null || playerRound === void 0 ? void 0 : playerRound.score) || [];
        var scoreOut = (playerRound === null || playerRound === void 0 ? void 0 : playerRound.scoreOut) || '';
        var scoreIn = (playerRound === null || playerRound === void 0 ? void 0 : playerRound.scoreIn) || '';
        var scoreTotal = (playerRound === null || playerRound === void 0 ? void 0 : playerRound.scoreTotal) || '';
        var scoreInfo = insertIntoArray(holeScores, 9, scoreOut);
        scoreInfo.push(scoreIn);
        scoreInfo.push(scoreTotal);
        if (holesToShow === Holes.FRONT9) {
            return insertIntoArray(scoreInfo.splice(0, 10), 11, '');
        }
        else if (holesToShow === Holes.BACK9) {
            return scoreInfo.splice(10);
        }
        return scoreInfo;
    };
    var getFront9Strokes = function () {
        return (playerRound === null || playerRound === void 0 ? void 0 : playerRound.strokes.slice(0, 9)) || [];
    };
    var getBack9Strokes = function () {
        return (playerRound === null || playerRound === void 0 ? void 0 : playerRound.strokes.slice(9)) || [];
    };
    var getStrokeClass = function (hole) {
        var toPar = playerRound.toParRaw[hole] ? playerRound.toParRaw[hole] : 0;
        switch (true) {
            case toPar >= 2:
                return 'double-bogey';
            case toPar === 1:
                return 'bogey';
            case toPar === -1:
                return 'birdie';
            case toPar <= -2:
                return 'eagle';
            default:
                return 'par';
        }
    };
    var roundHasHiddenHoles = function () {
        return hiddenHoleCount > 0;
    };
    var hideTotalScore = function () {
        var allStrokesNotVisible = playerRound.strokes.some(function (s) { return s === ''; });
        return roundHasHiddenHoles() && allStrokesNotVisible;
    };
    var renderStrokes = function (strokes, index) {
        var isHiddenHole = playerRound.hidden[index] === true;
        var strokesNotVisible = strokes === '';
        return isHiddenHole && strokesNotVisible ? (React.createElement("td", { key: index },
            React.createElement(HiddenHole, null))) : (React.createElement("td", { key: index, className: isHiddenHole ? 'hidden-hole' : '' },
            React.createElement("div", { className: getStrokeClass(index) }, strokes)));
    };
    var getRoundTitle = function () {
        return isMultiRound && holesToShow === '18'
            ? getTranslation(intl, 'roundShort', { round: roundNumber })
            : getTranslation(intl, 'score');
    };
    var getScoreTitle = function () {
        return gameFormatId && showPoints(gameFormatId) ? getTranslation(intl, 'points') : getTranslation(intl, 'net');
    };
    var maybeRenderPlayingHcp = function () {
        if (isMultiRound && playerRound.playingHcp !== null && !isScratch) {
            return React.createElement("span", null,
                "(PHCP ",
                convertPhcpToString(playerRound.playingHcp),
                ")");
        }
        return null;
    };
    var renderRoundDetails = function () {
        var _a;
        var startDate = playerRound.startTime ? getFormattedDate(playerRound.startTime, units, timezone) : false;
        var hcp = playerRound.hcp !== null ? convertHcpToString(playerRound.hcp) : false;
        var teebox = (_a = player.teeBox) !== null && _a !== void 0 ? _a : false;
        return (React.createElement("div", { className: "score-table-round-details" },
            startDate && React.createElement("span", null, startDate),
            hcp && React.createElement("span", null,
                "HCP ",
                hcp),
            teebox && React.createElement("span", null,
                "Tee: ",
                teebox)));
    };
    return (React.createElement("tbody", null,
        holesToShow !== Holes.BACK9 && (React.createElement("tr", { className: "score-table-round-course" },
            React.createElement("td", { className: "small", colSpan: 23 },
                React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between' } },
                    React.createElement("span", null, leaderboardRound === null || leaderboardRound === void 0 ? void 0 :
                        leaderboardRound.courseName,
                        " ",
                        maybeRenderPlayingHcp()),
                    isWeekly && renderRoundDetails())))),
        React.createElement("tr", { className: "score-table-round-par" },
            React.createElement("td", null, getTranslation(intl, 'par')),
            getParData().map(function (par, i) { return (React.createElement("td", { key: i }, par)); })),
        React.createElement("tr", { className: "score-table-round-strokes" },
            React.createElement("td", { className: "small bold" }, getRoundTitle()),
            holesToShow !== Holes.BACK9 &&
                getFront9Strokes().map(function (strokes, i) { return renderStrokes(strokes, i); }),
            holesToShow !== Holes.BACK9 && React.createElement("td", null, playerRound.strokesOut),
            holesToShow === Holes.FRONT9 && React.createElement("td", null),
            holesToShow !== Holes.FRONT9 &&
                getBack9Strokes().map(function (strokes, i) { return renderStrokes(strokes, i + 9); }),
            holesToShow !== Holes.FRONT9 && React.createElement("td", { className: "bold" }, hideTotalScore() ? '-' : playerRound.strokesIn),
            holesToShow !== Holes.FRONT9 && React.createElement("td", { className: "bold" }, hideTotalScore() ? '-' : playerRound.strokesTotal)),
        React.createElement("tr", { className: "score-table-round-score" },
            React.createElement("td", { className: "medium bold" }, getScoreTitle()),
            getScoreData().map(function (score, i) {
                var boldIndexes = holesToShow === Holes.BACK9 ? [9, 10] : [9, 19, 20];
                var className = boldIndexes.includes(i) ? 'bold' : '';
                return (React.createElement("td", { key: i, className: className }, score));
            }))));
}
